const config = {
  subtree: true,
  attributes: true
};

/**
 * NumberValuesObserver observer HTMLElement and format it's innerText to numeric format with a Intl.NumberFormat.
 *
 * NumberValuesObserver is initialized on page load and it's attached to HTMLElements witch has a class named '.number-values'.
 * All those elements are formatted by rules set in the data attributes.
 *
 * data-precision is amount of decimals shown.
 * data-unit is suffix added to the end of formatted number
 * data-value is attribute that is being observer, to make change to the element change this value
 * data-emptyvalue for replacing zero values with something else.. like '-'
 *
 * @example
 * ```
 * <span class="number-value" data-precision="2" data-unit="€" data-value="100000.123">100 000.12 €</span>
 * ```
 *
 * @example
 * ```
 * <span class="number-value" data-precision="2" data-unit="€/kk" data-value="0" data-emptyvalue="-">- €/kk</span>
 * ```
 */
class NumberValuesObserver {

  /**
   * Do not instantiate by your self, this is done during the page load in the static init function.
   * @constructor
   * @param {NodeList} elements HTMLElements from querySelector
   */
  constructor( elements ) {

    this._observer = new MutationObserver( this._onMutation.bind(this) );

    elements.forEach(( element ) => {

      element.precision = parseInt(element.getAttribute('data-precision')) || 0;

      element.formatter = new Intl.NumberFormat('fi-FI', {
        style: 'decimal',
        minimumFractionDigits: element.precision,
        maximumFractionDigits: element.precision,
      });

      element.read = this.read;
      element.render = this.render;

      this._observer.observe(element, config);

      //Render stuff once in a page load
      element.render( element.read() );
    });
  }

  /**
   * Callback handler for mutationObserver
   * @param  {object} mutationRecord https://developer.mozilla.org/en-US/docs/Web/API/MutationObserver
   * @param  {object} observer       https://developer.mozilla.org/en-US/docs/Web/API/MutationObserver
   */
  _onMutation(mutationRecord, observer) {
    for (const event of mutationRecord) {
      if (event.attributeName === 'data-value') {
        event.target.render(event.target.read());
      }
    }
  }

  /**
   * Returns the internal value
   * @return {float} internal value
   */
  read() {
    return parseFloat(this.getAttribute('data-value'));
  }

  /**
   * Renders the external value display
   * @param  {float} value numeric value
   */
  render(value) {
    const unit = this.dataset.unit ? ' ' + this.dataset.unit : '';
    const emptyValue = this.getAttribute('data-emptyvalue') ?? 0;
    const result = this.formatter.formatToParts(value).map(({type, value}) => {
      switch (type) {
      case 'nan': return emptyValue;
      default : return `${value}`;
      }
    }).reduce((string, part) => string + part);

    this.innerText = result + unit;
  }

  /**
   * Called after page load
   */
  static init() {
    new NumberValuesObserver( document.querySelectorAll('.number-value') );
  }
}

export default NumberValuesObserver;
